// Google Ads conversion tracking (gtag.js).
//
// Dormant until the two constants below are filled in: with GOOGLE_ADS_ID
// empty the script is never injected and every function here is a no-op, so
// this ships to production with literally zero effect. Filling them in and
// rebuilding is the whole activation step.
//
// These are NOT secrets — the conversion ID is visible in the page source of
// every site running Google Ads. They live in the repo rather than a VITE_
// env var on purpose: Vite bakes env vars in at *build* time, so a future
// build from a machine without the .env would silently disable conversion
// tracking with no error. A constant can't fail that way.
//
// CONSENT: this deliberately does NOT implement Google Consent Mode v2.
// Consent Mode only means something alongside a consent banner, and the site
// has none (Microsoft Clarity runs on the same footing today). Google requires
// Consent Mode v2 for EEA traffic, which is why the ads plan drops Germany and
// France from geo targeting. **Add a banner + Consent Mode before targeting
// any EEA country.**

/** e.g. 'AW-123456789'. Empty = tracking disabled entirely. */
export const GOOGLE_ADS_ID = 'AW-18334402616'

/** Per-action conversion labels from the Google Ads UI. Empty = that specific
 *  conversion doesn't fire; the others still work. */
export const CONVERSION_LABELS: Record<ConversionName, string> = {
  purchase: 'U2zwCNWzrtocELiQw6ZE',
  checkout_started: 'PGmICJ3xttocELiQw6ZE',
  checkout_email_entered: 'TGU7CKDxttocELiQw6ZE',
  plan_clicked: 'VdnjCKPxttocELiQw6ZE',
}

export type ConversionName =
  | 'purchase'
  | 'checkout_started'
  | 'checkout_email_entered'
  | 'plan_clicked'

type GtagFn = (...args: unknown[]) => void
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: GtagFn
  }
}

let initialised = false

/** Inject gtag.js once, at app start. Called from main.tsx.
 *
 *  Loading at startup rather than at conversion time is deliberate: gtag reads
 *  `gclid` off the landing URL and writes the `_gcl_aw` cookie. By the time
 *  someone reaches the order-confirmation page they've navigated several times
 *  and that parameter is long gone, so a late-loading tag would attribute far
 *  fewer conversions. */
export function initGtag(): void {
  if (initialised || !GOOGLE_ADS_ID || typeof window === 'undefined') return
  initialised = true

  window.dataLayer = window.dataLayer || []
  // Must be a real function (not an arrow assigned later) so the `arguments`
  // object is what gtag.js expects.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  } as GtagFn

  window.gtag('js', new Date())
  window.gtag('config', GOOGLE_ADS_ID)

  // Everything above is free — it only fills a queue. The 148 KB download is
  // not, and it contended with the app bundle for the single connection that
  // gates first paint. Deferring it to after paint measured 75 -> 91 on mobile
  // Lighthouse for /destinations/cn.
  //
  // This does NOT weaken gclid capture, which is the reason the tag loads at
  // app start at all. No landing page rewrites the URL, so gtag.js still reads
  // gclid off window.location and writes _gcl_aw when it lands a few hundred ms
  // later, and the `config` queued above replays at that point.
  const load = () => {
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GOOGLE_ADS_ID)}`
    document.head.appendChild(s)
  }
  const defer = (window as unknown as { __nvDeferTag?: (fn: () => void) => void }).__nvDeferTag
  if (defer) defer(load)
  else load()
}

// --- de-duplication -------------------------------------------------------
//
// Google dedupes server-side on transaction_id, which is the real guarantee.
// This local guard is belt-and-braces: it stops us re-sending on a refresh of
// /order/<ref>, keeps the network quiet, and makes the behaviour observable in
// devtools. Capped so it can't grow without bound.

const SENT_KEY = 'nimvoy.gads.sent'
const SENT_CAP = 25

function alreadySent(txnId: string): boolean {
  try {
    const raw = localStorage.getItem(SENT_KEY)
    return raw ? (JSON.parse(raw) as string[]).includes(txnId) : false
  } catch {
    return false // storage blocked (private mode, ITP) — fall back to Google's dedupe
  }
}

function markSent(txnId: string): void {
  try {
    const raw = localStorage.getItem(SENT_KEY)
    const list = raw ? (JSON.parse(raw) as string[]) : []
    if (!list.includes(txnId)) list.push(txnId)
    localStorage.setItem(SENT_KEY, JSON.stringify(list.slice(-SENT_CAP)))
  } catch {
    /* nothing we can do; Google's transaction_id dedupe still applies */
  }
}

/** Fire the Purchase conversion. Safe to call more than once for the same
 *  order — that's the point. The confirmation page calls it twice by design:
 *  once when Stripe confirms payment, once when the eSIM reaches `delivered`.
 *  Measured on production, ~7% of delivered orders never reach the second
 *  trigger because the customer's tab is gone before fulfilment completes, so
 *  firing on payment alone-or-delivery alone both under-report. */
export function gtagPurchase(opts: {
  transactionId: string
  valueCents: number
  currency: string
}): void {
  const label = CONVERSION_LABELS.purchase
  if (!GOOGLE_ADS_ID || !label || !window.gtag) return
  if (!opts.transactionId || alreadySent(opts.transactionId)) return
  markSent(opts.transactionId)
  window.gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    value: opts.valueCents / 100,
    currency: (opts.currency || 'usd').toUpperCase(),
    transaction_id: opts.transactionId,
  })
}

/** Fire one of the Secondary conversions. These exist so there's something
 *  readable in week one — purchases will be 0–3/month at launch volume. They
 *  are marked Secondary in the Ads UI so they never influence bidding. */
export function gtagConversion(name: Exclude<ConversionName, 'purchase'>): void {
  const label = CONVERSION_LABELS[name]
  if (!GOOGLE_ADS_ID || !label || !window.gtag) return
  window.gtag('event', 'conversion', { send_to: `${GOOGLE_ADS_ID}/${label}` })
}
